import { useState, useEffect, FunctionComponent } from 'react';
import { Button } from '../../tailwindcomponents';
import { Autocomplete, TextField, Typography, Select, SelectMultiple, Table, TableRow, TableCell } from './../../tailwindcomponents';
import { CertificateAircraftCategory, CertificateData, CertificateRating, Certificates, MxiLicenseCode, License } from './../../types/certificates';
import { CheckCircle, RemoveCircle, Info } from '../../components/icons';
import Certificate from './Certificate';
import { EmployeeSearch, ProfileImg } from '../../components';
import { useSelector } from 'react-redux'
import { RootState } from '../../reducer';
import CertificatesApi from './../../api/certificates';
import { TableColumns } from '../../types/table';

type CertificateProps = {
    setLoading: Function,
    userType: string,
    ratings: CertificateRating[],
    categories: CertificateAircraftCategory[],
    catByRatingString:  Record<string, string[]>,
    licenses: License[],
}

type FlattenedCertificateObjects = {
    mechanic_full_name: string | undefined;	
    rating_name: string | undefined;
    aircraft_category_name: string | undefined;
    expiration_date_text: string | undefined;
    certificate_id: number | undefined;
}

// GetAllValidCertificates takes a sort key and direction but has no paging parameters and
// returns the whole list, so the table sorts client-side and these only need to stay constant.
// These are the values the request has always carried.
const SERVER_SORT_KEY = "Issue Date";
const SERVER_SORT_ASCENDING = false;

const sortTableColumns = [
    { label: "Employee Name", key: "mechanic_full_name" },
    { label: "Approval Date", key: "quality_assurance_accepted_date" },
    { label: "Expiration Date", key: "certificate_expired_date" },
    { label: "Status", key: "expired_date_status" },
] as TableColumns[];

const AllCertificates: FunctionComponent<CertificateProps> = ({ setLoading, userType, ratings, categories, catByRatingString, licenses }) => {
    const [allCertificates, setAllCertificates] = useState<Array<Certificates>>([]);
    const [selectedCertificate, setSelectedCertificate] = useState<CertificateData>();
    const [selectedType, setSelectedType] = useState<string>("All Certificates");
    const [editRights, setEditRights] = useState<boolean>(true);
    const store = useSelector((state: RootState) => state);
    const allUsers = store.employees.all_employees;
    const [filteredCertificates, setFilteredCertificates] = useState<Array<Certificates>>([]);
    const [selectedFilteredRatings, setSelectedFilteredRatings] = useState<string[]>([]);
    const [selectedFilteredAirCrafts, setSelectedFilteredAircrafts] = useState<string[]>([]);
    const isCertificateEditor = store.user.me.user_clearence_groups.includes("Certificates Create");
    const [tempRating, setTempRating] = useState<string | null>(null);

    const allowedCategories: string[] =
    tempRating
        ? catByRatingString?.[tempRating] ?? []
        : [];

    const fetchFilteredCertificates = async () => {
        const certificates = await CertificatesApi.GetAllFilteredCertificatesByIds(filteredCertificates.map(cert => cert.certificate_id));
        const preppedCertificates = flattenArray(certificates.data);
        downloadCSV(preppedCertificates, "Certificates download");
    }

    const flattenArray = (certificateArray: CertificateData[]): FlattenedCertificateObjects[] => {
        return certificateArray.flatMap(({ mechanic_full_name, certificate_rights, certificate_id}) =>
            certificate_rights.map(({ rating_name, aircraft_category_name, expiration_date_text }) => ({
                mechanic_full_name,
                rating_name,
                aircraft_category_name,
                expiration_date_text,
                certificate_id: certificate_id
            }))
        );
    }

    const filterCert = (type: string, ratings: string[], allCert: Certificates[], aircrafts: string[]) => { 
        let filterCertificate = allCert;
        
        if(type === "Icelandair Employees") {
            filterCertificate = allCert.filter(cert => cert.company_id  === 1);
        }
        else if(type === "Subcontracts") {
            filterCertificate = allCert.filter(cert => cert.company_id  !== 1);
        }
 
        if(ratings.length > 0) {
            filterCertificate = filterCertRatingsObjects(ratings, filterCertificate);;
        }

        if(aircrafts.length > 0) {
            filterCertificate = filterCertAircraftObjects(aircrafts, filterCertificate);
        }

        setFilteredCertificates(filterCertificate);
    }

    useEffect(() => {
        setEditRights(isCertificateEditor);
        refresh();
    }, []);

    const refresh = async () => {
        setLoading(true);
        
        try {
            const certRes = await CertificatesApi.GetAllValidCertificates(SERVER_SORT_KEY, SERVER_SORT_ASCENDING, "All Certificates");
            setAllCertificates(certRes.data);
            setFilteredCertificates(certRes.data);
        
            filterCert(selectedType, selectedFilteredRatings, certRes.data, selectedFilteredAirCrafts);
        } catch (error) {
            console.error("Error fetching data", error);
        } finally {
            setLoading(false);
        }
    }

    const createNewCertificate = () => {
        setSelectedCertificate({ certificate_rights: [] });
    }
    const onTypeChange = (type: any) => {
        setSelectedType(type);
        filterCert(type, selectedFilteredRatings, allCertificates, selectedFilteredAirCrafts);
    }

    function filterCertRatingsObjects(filters: string[], certificates: Certificates[]): Certificates[] {
        return certificates.filter(obj => obj.ratings.some(rating => filters.includes(rating)));
    }

    function filterCertAircraftObjects(filters: string[], certificates: Certificates[]): Certificates[] {
        return certificates.filter(obj => obj.aircrafts.some(x => filters.includes(x)));
    }
    
    const onRatingChange = (ratings: any) => {
        setSelectedFilteredRatings(ratings);
        filterCert(selectedType, ratings, allCertificates, selectedFilteredAirCrafts);
    }

    const onAircraftChange = (aircrafts: any) => {
        setSelectedFilteredAircrafts(aircrafts);
        filterCert(selectedType, selectedFilteredRatings, allCertificates, aircrafts);
    }

    const generateTableBody = (row: Certificates, index: number) => {
        return (
            <TableRow onClick={() => onRowClick(row)} >
                <TableCell>{index}</TableCell>
                <TableCell >
                    <div className="flex items-center min-w-0">
                        <ProfileImg imgKey={index} imgSize="xs" userName={allUsers.find(s => s.Id === row.mechanic_employee_id)?.userName?.split('@')[0]} />
                        <Typography align="left" display="inline" text={row.mechanic_full_name}></Typography>
                    </div>
                </TableCell>
                <TableCell>{row.quality_assurance_accepted_date_text}</TableCell>
                <TableCell>{row.certificate_expired_date_text} </TableCell>
                <TableCell>
                    {row.expired_date_status === 'Valid' ? <CheckCircle className="text-green-600" />
                        :
                        row.expired_date_status === 'Expiring' ? <Info color="secondary" />
                            : row.certificate_expired_date_text !== "" ?
                            <RemoveCircle color="error" /> : ""
                    }
                </TableCell>
            </TableRow>
        )
    }
    const onRowClick = (row: Certificates) => {
        CertificatesApi.GetCertificateById(row.certificate_id).then(res => {
            setSelectedCertificate({
                ...res.data,
                certificate_expired_date_text: row.certificate_expired_date_text,
                expired_date_status: row.expired_date_status,
            });
        })
    }
    const closeCertificate = () => {
        setSelectedCertificate(undefined);
        refresh();
    }
    const onSelectedEmployeeRow = (employeeId: number) => {
        let tempCertificate;

        setSelectedFilteredRatings([]);
        setSelectedType("All Certificates");

        if (employeeId === null || employeeId === 0) {
            tempCertificate = allCertificates;
            setFilteredCertificates(tempCertificate);
        }
        else {
            const selectedEmployee = allUsers.filter(s => s.user_id === employeeId);

            if(selectedEmployee.length === 0){
                // if the employee is not found in the list of all employees, then the employee is deactivated and we need to get his certificate from the database
                CertificatesApi.GetMechanicsValidCertificatesByEmployeeId(employeeId).then(res => {
                    tempCertificate = res.data;
                    setFilteredCertificates(tempCertificate);
                });
            }
            else{
                tempCertificate = allCertificates.filter(cert => cert.mechanic_full_name === selectedEmployee[0].name);
                setFilteredCertificates(tempCertificate);
            }
        }
        
    }
    return (
        <div className="w-full min-w-0">
            {selectedCertificate === undefined ? <div className="w-full min-w-0">
                <div className="ds-filter-bar">
                    <EmployeeSearch
                        place_holder={"Write employees name"}
                        showDisabledEmployees
                        onSelectedEmployee={(employeeId: number) => onSelectedEmployeeRow(employeeId)}
                    />
                    <Select
                        onSelectedItem={(e: any) => onTypeChange(e.target.value)}
                        selectedItem={selectedType}
                        width={75}
                        widthPercentage={true}
                        filter_by_text={"Certificate Type"}
                        options={["All Certificates", "Icelandair Employees", "Subcontracts"]}
                    />
                    <Autocomplete
                        fullWidth
                        options={ratings?.map(r => r.rating) ?? []}
                        value={tempRating ?? ''}
                        noOptionsText="No Ratings Found"
                        clearOnEscape
                        autoHighlight
                        onChange={(_, newVal) => {
                            const safeVal = newVal ?? '';
                            setTempRating(safeVal);
                            setSelectedFilteredAircrafts([]);
                            onRatingChange(safeVal);
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Ratings" margin="none" variant="outlined" size="medium" fullWidth className="[&_fieldset]:border-none" />
                        )}
                    />
                    <SelectMultiple
                        onSelectedItem={(e: any) => onAircraftChange(e.target.value)}
                        selectedItemsString={selectedFilteredAirCrafts}
                        widthPercentage={true}
                        width={100}
                        filter_by_text={"Aircrafts"}
                        options={allowedCategories}
                    />
                    {/* Same idiom as ChecklistsOverview: justify-start overrides the
                      * flex-end default so the buttons hug the filters instead of the
                      * page edge, and spanning two tracks from sm up keeps both on one
                      * line - in a single 150px track they wrapped. Below sm the grid is
                      * one column, where a 2-track span would add an implicit column. */}
                    <div className="ds-filter-actions justify-start sm:col-span-2">
                        {editRights === true &&
                            <Button variant="contained" onClick={() => createNewCertificate()} color="primary">New Certificate</Button>
                        }
                        <Button variant="contained" color="primary" onClick={() => fetchFilteredCertificates()} >Export to csv</Button>
                    </div>
                </div>
                <div className="w-full min-w-0">
                    <Table
                        sortTableColumns={sortTableColumns}
                        rows={filteredCertificates}
                        generateBody={(row: any, index: number) => generateTableBody(row, index)}
                        defaultRowsPerPage={25}
                    />
                </div>
            </div>
                :
                <div>
                    <Certificate
                        licenses={licenses}
                        catByRatingString={catByRatingString}
                        ratings={ratings}
                        categories={categories}
                        setLoading={(loadingStatus: boolean) => setLoading(loadingStatus)}
                        selectedCertificate={selectedCertificate}
                        setSelectedCertificate={setSelectedCertificate}
                        closeCertificate={() => closeCertificate()}
                        viewType={editRights ? 'edit' : 'view'}
                        userType={userType}
                    />
                </div>
            }
        </div>
    );
};
export default AllCertificates;

const convertToCSV = (data: FlattenedCertificateObjects[]): string => {
    const csvRows: string[] = [];

    const headers = ['name', 'rating', 'aircraft', 'exp_date', 'certificate id'];
    csvRows.push(headers.join(';')); // Change delimiter to semicolon

    for (const row of data) {
        const values = [
            `"${row.mechanic_full_name}"`, 
            `"${row.rating_name}"`, 
            `"${row.aircraft_category_name}"`, 
            `"${row.expiration_date_text}"`,
            `"${row.certificate_id}"`,
        ];
        csvRows.push(values.join(';')); // Change delimiter to semicolon
    }

    return csvRows.join('\n');
}

const downloadCSV = (data: FlattenedCertificateObjects[], filename: string) => {
    const csv = convertToCSV(data);

    const bom = "\uFEFF";
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}
